import { useState } from "react";
import { RadioGroup } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/solid";
import { SparklesIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";

import {
  SubscriptionPlan,
  BillingFrequency,
  PaymentMethod,
  useStartCheckout,
  useChangeSubscription,
} from "hooks/useStripe";
import { InvoiceCheckoutModal } from "components/Billing/InvoiceCheckoutModal";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const frequencies: {
  value: BillingFrequency;
  label: string;
  priceSuffix: string;
}[] = [
  { value: BillingFrequency.Month, label: "Monatlich", priceSuffix: "/Monat" },
  { value: BillingFrequency.Year, label: "Jährlich", priceSuffix: "/Jahr" },
];

const paymentMethods: { value: PaymentMethod; label: string }[] = [
  { value: PaymentMethod.Card, label: "settings.billing.pm_card" },
  { value: PaymentMethod.Sepa, label: "settings.billing.pm_sepa" },
  { value: PaymentMethod.Invoice, label: "settings.billing.pm_invoice" },
];

const tiers = [
  {
    name: "Einzelkämpfer",
    id: SubscriptionPlan.Free,
    price: { month: "€159", year: "€1.908" },
    description: "Die Basis-Version für Einsteiger.",
    features: [
      "1 User",
      "30 Tage Testversion",
      "Monatlich kündbar",
      "Täglich aktualisierte Normen",
      "Richtlinienrecherche",
      "10 Jahre Daten-Speicherung",
    ],
    mostPopular: false,
    continuous: false,
  },
  {
    name: "Dienstleister",
    id: SubscriptionPlan.ProMd,
    price: { month: "€318", year: "€3.816" },
    description: "Der beste Preis für Ingenieurbüros.",
    features: [
      "2 User",
      "30 Tage Testversion",
      "Monatlich kündbar",
      "Unbegrenzte Projekte",
      "10 Jahre Daten-Speicherung",
      "48-hour support response time",
    ],
    mostPopular: false,
    continuous: true,
  },
  {
    name: "Mittelständer",
    id: SubscriptionPlan.ProLg,
    price: { month: "€954", year: "€11.448" },
    description: "Für Ihren Wachstum.",
    features: [
      "5 User",
      "30 Tage Testversion",
      "Täglich aktualisierte Normen",
      "Richtlinienrecherche",
    ],
    mostPopular: true,
    continuous: true,
  },
];

interface Props {
  currentPlan?: SubscriptionPlan;
  currentFrequency?: BillingFrequency;
}

export const PlanSelector = ({ currentPlan, currentFrequency }: Props) => {
  const { t } = useTranslation();
  const [frequency, setFrequency] = useState(frequencies[0]);
  const [paymentMethod, setPaymentMethod] = useState(paymentMethods[0]);

  const isYearly = frequency.value === BillingFrequency.Year;

  const handleFrequencyChange = (newFrequency: typeof frequency) => {
    setFrequency(newFrequency);
    if (
      newFrequency.value === BillingFrequency.Month &&
      paymentMethod.value === PaymentMethod.Invoice
    ) {
      setPaymentMethod(paymentMethods[0]);
    }
  };
  const [invoiceModalPlan, setInvoiceModalPlan] = useState<SubscriptionPlan | null>(null);
  const { startCheckout, isLoading: isCheckoutLoading } = useStartCheckout();
  const { changeSubscription, isLoading: isChangeLoading } =
    useChangeSubscription();

  const hasActivePlan =
    currentPlan &&
    currentPlan !== SubscriptionPlan.Free;

  const handleSelect = (plan: SubscriptionPlan) => {
    if (plan === SubscriptionPlan.Free) return;

    if (hasActivePlan) {
      changeSubscription({ plan, frequency: frequency.value });
    } else if (paymentMethod.value === PaymentMethod.Invoice) {
      setInvoiceModalPlan(plan);
    } else {
      startCheckout({
        plan,
        frequency: frequency.value,
        paymentMethod: paymentMethod.value,
      });
    }
  };

  const getCtaLabel = (tierId: SubscriptionPlan) => {
    if (tierId === SubscriptionPlan.Free) return t("settings.billing.status_free");
    if (hasActivePlan && tierId === currentPlan) return t("settings.billing.status_active");
    if (hasActivePlan) return t("settings.billing.action_change");
    return t("settings.billing.action_upgrade");
  };

  const isCurrentPlan = (tierId: SubscriptionPlan) =>
    hasActivePlan && tierId === currentPlan;

  return (
    <div>
      <div className="mx-auto">
        <div className="flex flex-wrap items-center justify-center gap-4">
          <RadioGroup
            value={frequency}
            onChange={handleFrequencyChange}
            className="grid grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs font-semibold leading-5 ring-1 ring-inset ring-gray-200 bg-white shadow-sm"
          >
            <RadioGroup.Label className="sr-only">
              {t("settings.billing.frequency_label")}
            </RadioGroup.Label>
            {frequencies.map((option) => (
              <RadioGroup.Option
                key={option.value}
                value={option}
                className={({ checked }) =>
                  classNames(
                    checked
                      ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-md"
                      : "text-gray-500 hover:text-gray-700",
                    "cursor-pointer rounded-full px-4 py-1.5 transition-all"
                  )
                }
              >
                <span>{option.label}</span>
              </RadioGroup.Option>
            ))}
          </RadioGroup>

          {!hasActivePlan && (
            <RadioGroup
              value={paymentMethod}
              onChange={setPaymentMethod}
              className="grid grid-cols-3 gap-x-1 rounded-full p-1 text-center text-xs font-semibold leading-5 ring-1 ring-inset ring-gray-200 bg-white shadow-sm"
            >
              <RadioGroup.Label className="sr-only">
                {t("settings.billing.pm_label")}
              </RadioGroup.Label>
              {paymentMethods.map((option) => {
                const isDisabled =
                  option.value === PaymentMethod.Invoice && !isYearly;

                return (
                  <RadioGroup.Option
                    key={option.value}
                    value={option}
                    disabled={isDisabled}
                    className={({ checked }) =>
                      classNames(
                        checked
                          ? "bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-md"
                          : isDisabled
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-500 hover:text-gray-700",
                        "rounded-full px-4 py-1.5 transition-all",
                        isDisabled ? "" : "cursor-pointer"
                      )
                    }
                  >
                    <span>{t(option.label)}</span>
                  </RadioGroup.Option>
                );
              })}
            </RadioGroup>
          )}
        </div>

        <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-6 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={classNames(
                tier.mostPopular
                  ? "ring-2 ring-blue-500 shadow-lg shadow-blue-500/10 scale-[1.02]"
                  : isCurrentPlan(tier.id)
                  ? "ring-2 ring-cyan-600 shadow-md"
                  : "ring-1 ring-gray-200 shadow-sm hover:shadow-md hover:ring-gray-300",
                "relative bg-white rounded-2xl p-8 xl:p-10 transition-all duration-200"
              )}
            >
              {tier.mostPopular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 px-4 py-1 text-xs font-semibold text-white shadow-md">
                    <SparklesIcon className="h-3.5 w-3.5" />
                    {t("settings.billing.most_popular")}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between">
                <h3
                  id={tier.id}
                  className="text-lg font-semibold leading-8 text-gray-900"
                >
                  {tier.name}
                </h3>
                {isCurrentPlan(tier.id) && (
                  <span className="inline-flex items-center rounded-full bg-cyan-50 px-2.5 py-0.5 text-xs font-medium text-cyan-700 ring-1 ring-inset ring-cyan-600/20">
                    {t("settings.billing.current_plan")}
                  </span>
                )}
              </div>

              <p className="mt-3 text-sm leading-6 text-gray-500">
                {tier.description}
              </p>

              <div className="mt-6 flex items-baseline gap-x-1">
                <span
                  className={classNames(
                    tier.mostPopular ? "text-blue-600" : "text-gray-900",
                    "text-4xl font-bold tracking-tight"
                  )}
                >
                  {tier.price[frequency.value]}
                </span>
                <span className="text-sm font-medium leading-6 text-gray-400">
                  {frequency.priceSuffix}
                </span>
              </div>

              <button
                onClick={() => handleSelect(tier.id)}
                disabled={
                  tier.id === SubscriptionPlan.Free ||
                  isCurrentPlan(tier.id) ||
                  isCheckoutLoading ||
                  isChangeLoading
                }
                aria-describedby={tier.id}
                className={classNames(
                  isCurrentPlan(tier.id)
                    ? "bg-gray-100 text-gray-400 cursor-default"
                    : tier.id === SubscriptionPlan.Free
                    ? "bg-gray-100 text-gray-400 cursor-default"
                    : tier.mostPopular
                    ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md shadow-blue-500/25 hover:shadow-lg hover:shadow-blue-500/30 focus-visible:outline-blue-600"
                    : "bg-gray-900 text-white shadow-sm hover:bg-gray-800 focus-visible:outline-gray-900",
                  "mt-6 block w-full rounded-lg py-2.5 px-3 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 transition-all"
                )}
              >
                {isCheckoutLoading || isChangeLoading
                  ? "..."
                  : getCtaLabel(tier.id)}
              </button>

              <div className="mt-8 border-t border-gray-100 pt-6">
                <ul
                  role="list"
                  className="space-y-3 text-sm leading-6 text-gray-600"
                >
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex gap-x-3">
                      <CheckIcon
                        className={classNames(
                          tier.mostPopular
                            ? "text-blue-500"
                            : "text-cyan-500",
                          "h-5 w-5 flex-none"
                        )}
                        aria-hidden="true"
                      />
                      {feature}
                    </li>
                  ))}
                  {tier.continuous && (
                    <li className="flex gap-x-3">
                      <SparklesIcon
                        className={classNames(
                          tier.mostPopular
                            ? "text-blue-500"
                            : "text-cyan-500",
                          "h-5 w-5 flex-none"
                        )}
                        aria-hidden="true"
                      />
                      Stetig neue Funktionen
                    </li>
                  )}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {invoiceModalPlan && (
        <InvoiceCheckoutModal
          show={!!invoiceModalPlan}
          plan={invoiceModalPlan}
          frequency={frequency.value}
          onClose={() => setInvoiceModalPlan(null)}
        />
      )}
    </div>
  );
};
